'use client';
import { KeyRound } from 'lucide-react';
import type { PluginConfigEditorDetail } from 'elowen-plugin-ui-kit';
import { StatusMark } from '../../components/ui/StatusMark';
import { buttonClassName } from '../../components/ui/Button';
import { SettingsGroup } from '../../components/ui/SettingsSurface';
import { ShellLink } from '../../components/shell/ShellLink';
import { useTranslation } from '../../lib/i18n';
import { ROW_ANCHOR_PARAM } from '../../lib/rowAnchors';
import { requiredConfigFieldMissing } from '../../lib/usePluginConfigDraft';
import type { PluginCredentialsSummary } from '../../lib/types';
import { settingsSectionHref } from './categories';

/** Settings → Access is built only from what a manifest declares: `detail.credentials` comes from the
 *  daemon's `credentialFieldKeys` (every secret plus `credential: true` companions). Nothing here may
 *  name a plugin or a field. */
export type PluginAccessStatus = 'reconnect' | 'missing' | 'connected' | 'unset';

/** The one status rule for a plugin's credentials, read by the Access row and the plugin detail card.
 *  A corrupt secret outranks everything (it must be entered again); a missing required credential comes
 *  next; otherwise the plugin is connected once any credential holds a value. */
export function pluginAccessStatus(
  detail: Pick<PluginConfigEditorDetail, 'configSchema' | 'secretsSet'>,
  credentials: PluginCredentialsSummary,
  values: Record<string, unknown>,
): PluginAccessStatus {
  if (credentials.corrupt.length > 0) return 'reconnect';
  const fields = detail.configSchema.filter((field) => credentials.keys.includes(field.key));
  if (fields.some((field) => requiredConfigFieldMissing(field, values, detail.secretsSet))) return 'missing';
  const holdsValue = fields.some((field) => field.type === 'secret'
    ? detail.secretsSet.includes(field.key)
    : values[field.key] != null && String(values[field.key]).trim() !== '');
  return holdsValue ? 'connected' : 'unset';
}

/** The anchor a plugin's Access row carries. `useRowAnchor` opens the folded row around it. */
export const accessRowId = (plugin: string): string => `access.${plugin}`;

/** The deep link that opens Settings → Access with this plugin's row expanded. */
export const accessHref = (plugin: string): string =>
  `${settingsSectionHref('access')}&${ROW_ANCHOR_PARAM}=${encodeURIComponent(accessRowId(plugin))}`;

const STATUS_TONE = { reconnect: 'danger', missing: 'warning', connected: 'success', unset: 'muted' } as const;

export function PluginAccessBadge({ status }: { status: PluginAccessStatus }) {
  const { t } = useTranslation();
  const label = {
    reconnect: t.access.statusReconnect,
    missing: t.access.statusMissing,
    connected: t.access.statusConnected,
    unset: t.access.statusUnset,
  }[status];
  if (status === 'connected') return null;
  return <span data-access-status={status}><StatusMark kind={status === 'unset' ? 'waiting' : 'error'} tone={STATUS_TONE[status]} label={label} /></span>;
}

/** What an instance editor shows in place of the credential fields it no longer renders: their status
 *  and the one way to the place they are edited. */
export function PluginAccessCard({ plugin, status }: { plugin: string; status: PluginAccessStatus }) {
  const { t } = useTranslation();
  return (
    <SettingsGroup
      icon={KeyRound}
      title={t.settings.access}
      description={t.access.cardDescription}
      actions={(
        <>
          <PluginAccessBadge status={status} />
          <ShellLink href={accessHref(plugin)} className={buttonClassName('default', 'sm')} data-access-link={plugin}>
            {t.access.open}
          </ShellLink>
        </>
      )}
    />
  );
}
